import { Platform } from '@/constants/main';
import { getAccountUiPlatformIds, platformDictionary } from '@/stores/simpleDictionary/platform';
import type { DataTableHeader } from '@/types/vuetify';

export function createAccountsPageHeaders(): DataTableHeader[] {
    const { t } = useI18n();
    const getPlatformLabelForAccountColumns = (platformId: number) => {
        if (platformId === Platform.Bolsadeaposta) {
            return 'Bolsa';
        }
        return platformDictionary.nameById(platformId) || String(platformId);
    };

    const base: DataTableHeader[] = [
        {
            title: '',
            key: 'data-table-expand',
            value: 'data-table-expand',
            align: 'center',
            sortable: false,
            minWidth: 28,
            width: 28,
            cellProps: { class: 'px-0' },
            headerProps: { class: 'px-0 account-header--bottom' },
        },
        {
            title: '',
            value: 'select',
            key: 'select',
            align: 'center',
            sortable: false,
            minWidth: 26,
            width: 26,
            cellProps: { class: 'px-0' },
            headerProps: { class: 'px-0 account-header--bottom' },
        },
        {
            title: t('accounts.columns.status'),
            value: 'status',
            key: 'status',
            align: 'start',
            sortable: false,
            nowrap: true,
            minWidth: 88,
            width: 88,
            headerProps: { class: 'account-header--bottom' },
        },
        {
            title: t('accounts.columns.login'),
            value: 'login',
            key: 'login',
            align: 'start',
            sortable: true,
            nowrap: true,
            minWidth: 80,
            width: 100,
            headerProps: { class: 'account-header--bottom' },
        },
        {
            title: t('accounts.columns.set'),
            value: 'set_name',
            key: 'set_name',
            align: 'center',
            sortable: true,
            nowrap: true,
            minWidth: 50,
            width: 56,
            headerProps: { class: 'account-header--bottom' },
        },
        {
            title: t('accounts.columns.currency'),
            value: 'currency_id',
            key: 'currency_id',
            align: 'center',
            sortable: true,
            nowrap: true,
            minWidth: 44,
            width: 48,
            headerProps: { class: 'account-header--bottom' },
        },
        {
            title: t('accounts.columns.date'),
            value: 'created_at',
            key: 'created_at',
            align: 'center',
            sortable: true,
            nowrap: true,
            minWidth: 88,
            width: 96,
            headerProps: { class: 'account-header--bottom' },
        },
        {
            title: t('accounts.columns.proxy'),
            value: 'proxy_name',
            key: 'proxy_name',
            align: 'center',
            sortable: true,
            nowrap: true,
            minWidth: 56,
            width: 72,
            headerProps: { class: 'account-header--bottom' },
        },
        {
            title: t('accounts.columns.credit'),
            value: 'balance',
            key: 'balance',
            align: 'end',
            sortable: true,
            nowrap: true,
            minWidth: 56,
            width: 64,
            headerProps: { class: 'account-header--bottom' },
        },
        {
            title: t('accounts.columns.validPerc'),
            value: 'valid_perc',
            key: 'valid_perc',
            align: 'end',
            sortable: true,
            nowrap: true,
            minWidth: 44,
            width: 48,
            headerProps: { class: 'account-header--bottom' },
        },
        {
            title: t('accounts.columns.bets'),
            value: 'bet_count',
            key: 'bet_count',
            align: 'center',
            sortable: true,
            nowrap: true,
            minWidth: 44,
            width: 48,
            headerProps: { class: 'account-header--bottom' },
        },
        {
            title: t('accounts.columns.sinceLastBet'),
            value: 'last_bet_at',
            key: 'last_bet_at',
            align: 'center',
            sortable: true,
            nowrap: true,
            minWidth: 64,
            width: 72,
            headerProps: { class: 'account-header--bottom' },
        },
        {
            title: t('accounts.columns.user'),
            value: 'tool_user_logins',
            key: 'tool_user_logins',
            align: 'center',
            sortable: false,
            nowrap: true,
            minWidth: 56,
            width: 72,
            headerProps: { class: 'account-header--bottom' },
        },
    ];

    const platformHeaders: DataTableHeader[] = [];
    for (const platformId of getAccountUiPlatformIds()) {
        const platformLabel = getPlatformLabelForAccountColumns(platformId);
        platformHeaders.push({
            title: `${platformLabel} ${t('accounts.columns.botAbbr')}`,
            value: `platform_bot_${platformId}`,
            key: `platform_bot_${platformId}`,
            align: 'center',
            sortable: false,
            nowrap: true,
            minWidth: 28,
            width: 32,
            maxWidth: 32,
            headerProps: { class: 'account-header--vertical' },
        });
        platformHeaders.push({
            title: `${platformLabel} ${t('accounts.columns.orderAbbr')}`,
            value: `platform_order_${platformId}`,
            key: `platform_order_${platformId}`,
            align: 'center',
            sortable: false,
            nowrap: true,
            minWidth: 28,
            width: 32,
            maxWidth: 32,
            headerProps: { class: 'account-header--vertical' },
        });
    }

    const tail: DataTableHeader[] = [
        {
            title: t('accounts.columns.parse'),
            value: 'used_for_parser',
            key: 'used_for_parser',
            align: 'center',
            sortable: false,
            nowrap: true,
            minWidth: 28,
            width: 32,
            maxWidth: 32,
            headerProps: { class: 'account-header--vertical' },
        },
        {
            title: t('accounts.columns.active'),
            value: 'is_active',
            key: 'is_active',
            align: 'center',
            sortable: false,
            nowrap: true,
            minWidth: 28,
            width: 32,
            maxWidth: 32,
            headerProps: { class: 'account-header--vertical' },
        },
        {
            title: t('accounts.columns.disabled'),
            value: 'is_disable',
            key: 'is_disable',
            align: 'center',
            sortable: false,
            nowrap: true,
            minWidth: 28,
            width: 32,
            maxWidth: 32,
            headerProps: { class: 'account-header--vertical' },
        },
        {
            title: '',
            value: 'actions',
            key: 'actions',
            align: 'end',
            sortable: false,
            nowrap: true,
            width: 144,
            cellProps: { class: 'pl-0 pr-1' },
        },
    ];

    return [...base, ...platformHeaders, ...tail];
}
